// Onboarding state management with localStorage persistence

export interface PeriodTime {
  name: string;
  startTime: string;
  endTime: string;
}

export interface RegistrationConfig {
  enabled: boolean;
  name: string;
  morningStartTime: string;
  morningDuration: number;
  afternoonEnabled: boolean;
  afternoonStartTime: string;
  afternoonDuration: number;
}

export interface PeriodConfig {
  numberOfPeriods: number;
  periods: PeriodTime[];
  periodDuration?: number;
  schoolStartTime?: string;
  breakTime: { startTime: string; endTime: string };
  lunchTime: { startTime: string; endTime: string };
  registration?: RegistrationConfig;
  transferTime?: number;              // Minutes between lessons (room-change / passing time)
  breakAfterPeriod?: number;          // Break comes after this period number (1-indexed, 0 = no break)
  breakDurationMins?: number;         // Break duration in minutes
  lunchAfterPeriod?: number;          // Lunch comes after this period number (0 = no lunch)
  lunchDurationMins?: number;         // Lunch duration in minutes
  /** AI-detected absolute lunch start time (HH:MM). Used by buildSchedule as an
   *  anchor so the lunch slot is placed at the exact visible time, not computed
   *  from period counting. Never overwritten by updatePc. */
  lunchStartOverride?: string;
  /** AI-detected absolute break start time (HH:MM). Same purpose as above. */
  breakStartOverride?: string;
  // legacy — kept for backward compat with SchoolStep5Config and API responses
  doublesPerDay?: number;
  passingTimeOverride?: number;
  shortDays?: Record<string, number>;
  sixthFormOverride?: { enabled: boolean; numberOfPeriods: number; periodDuration: number; schoolStartTime: string };
}

export interface SecondaryContact {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
}

export const DEFAULT_DEPARTMENTS = [
  'Arts',
  'Business Studies',
  'Computing & ICT',
  'Design & Technology',
  'English',
  'Humanities',
  'Mathematics',
  'Modern Foreign Languages',
  'Music',
  'PE & Sport',
  'PSHE / RSE',
  'Science',
  'SEND / Learning Support',
  'Social Sciences',
];

export interface SchoolData {
  schoolName: string;
  urn: string;
  dfeNumber: string;
  address: string;
  postcode: string;
  phone: string;
  contactFirstName: string;
  contactLastName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  secondaryContacts: SecondaryContact[];
  staff: StaffMember[];
  policies: PolicyDocument[];
  periods: string[];
  subjects: string[];
  departments?: string[];
  rooms: string[];
  yearGroups: string[];
  schoolTypes: string[];
  periodConfig: PeriodConfig;
  /** Rotation length. 1 = single-week (default). 2+ = rotating multi-week timetable.
   *  When > 1, additionalWeeks[0..weekPattern-2] hold weeks 2..N. No hard cap —
   *  the wizard offers quick-picks for 1–4 and a "More" input for longer rotations. */
  weekPattern?: number;
  /** Period configs for weeks 2..N. Length = weekPattern - 1 when set. */
  additionalWeeks?: PeriodConfig[];
  /** ISO date (Monday) that marks Week 1 of the rotation. Used to map any date
   *  → which week of the cycle it falls into. */
  weekAnchorDate?: string;
  customYearGroups: string[];
  logoUrl: string;
  primaryColor: string;
  gdprConsent: boolean;
  coverDeadline?: string;
  lastLessonEnd?: string;   // When the final bell rings / last teaching period ends
  gatesCloseTime?: string;  // When students are officially dismissed from the premises
  guestAccount?: { username: string; password: string; notes: string };
  /** Annual retention review for archived cover observations. Mirrors
   *  SchoolConfig.observationRetention (api.ts). Set in the wizard config step. */
  observationRetention?: {
    enabled?: boolean;
    reviewMonth?: number;
    reviewDay?: number;
    requireApproval?: boolean;
    graceDays?: number;
  };
}

export interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  phone?: string;
  /** Subjects this teacher is assigned to teach (subset of school subjects). Empty = all school subjects. */
  subjects?: string[];
  inviteCode: string;
  inviteSent: boolean;
  status?: 'pending' | 'activated' | 'terminated';
  invitedAt?: number;
  activatedAt?: number;
  lastLoginAt?: number;
}

export interface PolicyDocument {
  id: string;
  name: string;
  type: string;
  fileName: string;
  uploadedAt: string;
  fileData?: string;
}

export interface OnboardingState {
  isComplete: boolean;
  userType: 'school' | 'teacher' | 'cover' | null;
  currentStep: number;
  schoolData: Partial<SchoolData>;
  teacherCode: string;
  teacherName: string;
  teacherEmail: string;
  teacherDepartment: string;
  teacherPassword?: string; // For password login
  tutorialComplete: boolean;
  tutorialStep: number;
  sendInvitesLater: boolean;
  supabaseUserId?: string;  // School admin's Supabase user ID — stored after registration
}

// School credentials stored separately for login
export interface SchoolCredentials {
  email: string;
  password: string;
  schoolName: string;
  createdAt: number;
}

// Teacher credentials stored separately for login
export interface TeacherCredentials {
  code: string;
  email: string;
  password: string;
  name: string;
  department: string;
}

const TEACHER_CREDENTIALS_KEY = 'cmc_teacher_credentials';
const SCHOOL_CREDENTIALS_KEY = 'cmc_school_credentials';

const STORAGE_KEY = 'cmc_onboarding_state';
const VALID_CODES_KEY = 'cmc_valid_invite_codes';

const defaultState: OnboardingState = {
  isComplete: false,
  userType: null,
  currentStep: 0,
  schoolData: {
    staff: [],
    policies: [],
    secondaryContacts: [],
    periods: ['Period 1', 'Period 2', 'Period 3', 'Period 4', 'Period 5', 'Period 6'],
    subjects: ['English', 'Mathematics', 'Science', 'History', 'Geography', 'Art', 'Music', 'PE', 'Drama', 'Languages'],
    yearGroups: [],
    schoolTypes: [],
    customYearGroups: [],
    // Generic, NON-conflicting placeholder timetable. The period gaps are sized
    // to fit the break/lunch that buildSchedule inserts (prevPeriodEnd +
    // transferTime, for the full duration): 20 min before the post-P2 break
    // (5 transfer + 15 break) and 50 min before the post-P4 lunch (5 + 45).
    // Registration ends exactly when Period 1 begins, so nothing overlaps on
    // first landing. All timing knobs are set explicitly so the default stands
    // on its own rather than relying on downstream `??` fallbacks.
    periodConfig: {
      numberOfPeriods: 6,
      periodDuration: 60,
      schoolStartTime: '08:45',
      transferTime: 5,
      breakAfterPeriod: 2,
      breakDurationMins: 15,
      lunchAfterPeriod: 4,
      lunchDurationMins: 45,
      periods: [
        { name: 'Period 1', startTime: '08:45', endTime: '09:45' },
        { name: 'Period 2', startTime: '09:45', endTime: '10:45' },
        { name: 'Period 3', startTime: '11:05', endTime: '12:05' },
        { name: 'Period 4', startTime: '12:05', endTime: '13:05' },
        { name: 'Period 5', startTime: '13:55', endTime: '14:55' },
        { name: 'Period 6', startTime: '14:55', endTime: '15:55' },
      ],
      breakTime: { startTime: '10:50', endTime: '11:05' },
      lunchTime: { startTime: '13:10', endTime: '13:55' },
    },
    primaryColor: '#1e3a5f',
  },
  teacherCode: '',
  teacherName: '',
  teacherEmail: '',
  teacherDepartment: '',
  tutorialComplete: false,
  tutorialStep: 0,
  sendInvitesLater: false,
};

// ── Legacy-default schedule migration ────────────────────────────────
// The pre-July-2026 default periodConfig produced schedule conflicts the
// moment the wizard rendered (registration/break/lunch overlapped the
// computed periods). The default was fixed, but any browser that visited
// the wizard before the fix has the OLD default persisted in localStorage,
// which shadows the new one forever. Detect that exact untouched fingerprint
// and swap in the new default. A user-edited schedule can't match: editing
// any bubble/knob changes the times or stamps transferTime/breakAfterPeriod/
// override keys, all of which break the match.
const LEGACY_DEFAULT_PERIODS: ReadonlyArray<readonly [string, string]> = [
  ['08:30', '09:30'], ['09:30', '10:30'], ['10:45', '11:45'],
  ['11:45', '12:45'], ['13:30', '14:30'], ['14:30', '15:30'],
];

function isLegacyDefaultPeriodConfig(pc: PeriodConfig | undefined): boolean {
  if (!pc || !Array.isArray(pc.periods) || pc.periods.length !== 6) return false;
  // The old default never set these keys — any of them present means the
  // user has interacted with the schedule UI (or a scan ran). Don't touch.
  if (pc.transferTime !== undefined || pc.breakAfterPeriod !== undefined || pc.lunchAfterPeriod !== undefined) return false;
  if (pc.breakStartOverride || pc.lunchStartOverride) return false;
  if (pc.breakTime?.startTime !== '10:30' || pc.breakTime?.endTime !== '10:45') return false;
  if (pc.lunchTime?.startTime !== '12:45' || pc.lunchTime?.endTime !== '13:30') return false;
  return pc.periods.every((p, i) =>
    p.startTime === LEGACY_DEFAULT_PERIODS[i][0] && p.endTime === LEGACY_DEFAULT_PERIODS[i][1]);
}

export function getOnboardingState(): OnboardingState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const merged: OnboardingState = { ...defaultState, ...JSON.parse(stored) };
      if (merged.schoolData && isLegacyDefaultPeriodConfig(merged.schoolData.periodConfig)) {
        merged.schoolData = {
          ...merged.schoolData,
          // Deep copy so callers can't mutate the module-level default.
          periodConfig: JSON.parse(JSON.stringify(defaultState.schoolData.periodConfig)) as PeriodConfig,
        };
      }
      return merged;
    }
  } catch (e) {
    console.error('Failed to load onboarding state:', e);
  }
  return defaultState;
}

export function saveOnboardingState(state: Partial<OnboardingState>): void {
  try {
    const current = getOnboardingState();
    const newState = { ...current, ...state };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  } catch (e) {
    console.error('Failed to save onboarding state:', e);
  }
}

export function resetOnboarding(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(VALID_CODES_KEY);
  localStorage.removeItem('cmc_app_state');
  localStorage.removeItem('cmc_tutorial_state');
  localStorage.removeItem('cmc_invite_codes');
}

export function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Store valid invite codes for security validation
export interface ValidInviteCode {
  code: string;
  email: string;
  name: string;
  department: string;
  createdAt: number;
  used: boolean;
}

export function saveValidInviteCodes(codes: ValidInviteCode[]): void {
  try {
    localStorage.setItem(VALID_CODES_KEY, JSON.stringify(codes));
  } catch (e) {
    console.error('Failed to save invite codes:', e);
  }
}

export function getValidInviteCodes(): ValidInviteCode[] {
  try {
    const stored = localStorage.getItem(VALID_CODES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Failed to load invite codes:', e);
    return [];
  }
}

export function validateTeacherCode(code: string): { valid: boolean; teacher?: ValidInviteCode } {
  const codes = getValidInviteCodes();
  const found = codes.find(c => c.code.toUpperCase() === code.toUpperCase() && !c.used);
  if (found) {
    return { valid: true, teacher: found };
  }
  return { valid: false };
}

export function markCodeAsUsed(code: string): void {
  const codes = getValidInviteCodes();
  const updated = codes.map(c => 
    c.code.toUpperCase() === code.toUpperCase() ? { ...c, used: true } : c
  );
  saveValidInviteCodes(updated);
}

// Generate and save invite codes for all staff when school completes setup
export function generateAndSaveStaffCodes(staff: StaffMember[]): ValidInviteCode[] {
  const codes: ValidInviteCode[] = staff.map(member => ({
    code: member.inviteCode || generateInviteCode(),
    email: member.email,
    name: `${member.firstName} ${member.lastName}`,
    department: member.department,
    createdAt: Date.now(),
    used: false,
  }));
  
  saveValidInviteCodes(codes);
  return codes;
}

// Teacher credentials management
export function saveTeacherCredentials(creds: TeacherCredentials): void {
  try {
    const existing = getTeacherCredentials();
    const updated = existing.filter(c => c.email !== creds.email);
    updated.push(creds);
    localStorage.setItem(TEACHER_CREDENTIALS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save teacher credentials:', e);
  }
}

export function getTeacherCredentials(): TeacherCredentials[] {
  try {
    const stored = localStorage.getItem(TEACHER_CREDENTIALS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function validateTeacherPassword(email: string, password: string): { valid: boolean; teacher?: TeacherCredentials } {
  const credentials = getTeacherCredentials();
  const found = credentials.find(c => c.email.toLowerCase() === email.toLowerCase() && c.password === password);
  if (found) {
    return { valid: true, teacher: found };
  }
  return { valid: false };
}

export function hasTeacherAccount(email: string): boolean {
  const credentials = getTeacherCredentials();
  return credentials.some(c => c.email.toLowerCase() === email.toLowerCase());
}

// School credentials management
export function saveSchoolCredentials(creds: SchoolCredentials): void {
  try {
    localStorage.setItem(SCHOOL_CREDENTIALS_KEY, JSON.stringify(creds));
  } catch (e) {
    console.error('Failed to save school credentials:', e);
  }
}

export function getSchoolCredentials(): SchoolCredentials | null {
  try {
    const stored = localStorage.getItem(SCHOOL_CREDENTIALS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function validateSchoolPassword(email: string, password: string): { valid: boolean; school?: SchoolCredentials } {
  const creds = getSchoolCredentials();
  if (creds && creds.email.toLowerCase() === email.toLowerCase() && creds.password === password) {
    return { valid: true, school: creds };
  }
  return { valid: false };
}

export function hasSchoolAccount(): boolean {
  return getSchoolCredentials() !== null;
}
